import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import { AVATAR_UPLOAD_CONSTANTS, type IGeneratedImage } from '@/@types/avatars';

export function useAvatarUpload(createFolder: ReturnType<typeof useMutation>) {
  const navigate = useNavigate();

  const [isFileUploading, setIsFileUploading] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<IGeneratedImage | null>(null);
  const [uploadDimensions, setUploadDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [uploadFolderId, setUploadFolderId] = useState<Id<'avatarFolders'> | null>(null);
  const [uploadFolderTitle, setUploadFolderTitle] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  const generateUploadUrl = useMutation(api.generatedAvatars.generateUploadUrl);
  const uploadAvatarMutation = useMutation(api.generatedAvatars.uploadAvatar);

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (
        !AVATAR_UPLOAD_CONSTANTS.ACCEPTED_TYPES.includes(
          file.type as (typeof AVATAR_UPLOAD_CONSTANTS.ACCEPTED_TYPES)[number]
        )
      ) {
        alert('Please select a JPEG, PNG, or WebP image.');
        return;
      }
      if (file.size > AVATAR_UPLOAD_CONSTANTS.MAX_FILE_SIZE) {
        alert('File size must be under 20MB.');
        return;
      }
      setIsFileUploading(true);
      const url = URL.createObjectURL(file);
      const img = new window.Image();
      img.onerror = () => {
        URL.revokeObjectURL(url);
        setIsFileUploading(false);
        alert('Failed to load image.');
      };
      img.onload = async () => {
        try {
          const uploadUrl = await generateUploadUrl();
          const response = await fetch(uploadUrl, {
            method: 'POST',
            headers: { 'Content-Type': file.type },
            body: file,
          });
          if (!response.ok) throw new Error('Upload failed');
          const data: { storageId: string } = await response.json();
          setUploadedImage({ storageId: data.storageId as Id<'_storage'>, url });
          setUploadDimensions({ width: img.naturalWidth, height: img.naturalHeight });
          setUploadModalOpen(true);
        } catch {
          URL.revokeObjectURL(url);
          alert('Failed to upload image. Please try again.');
        } finally {
          setIsFileUploading(false);
        }
      };
      img.src = url;
      e.target.value = '';
    },
    [generateUploadUrl]
  );

  const handleUploadSave = useCallback(async () => {
    if (!uploadedImage || !uploadDimensions) return;
    setIsUploading(true);
    try {
      let folderId = uploadFolderId;
      if (!folderId && uploadFolderTitle.trim()) {
        const result = await createFolder({ title: uploadFolderTitle.trim() });
        if (!result.success || !result.folderId) {
          alert(result.error || 'Failed to create folder');
          return;
        }
        folderId = result.folderId;
      }
      if (!folderId) {
        alert('Please select or create a folder.');
        return;
      }
      await uploadAvatarMutation({
        storageId: uploadedImage.storageId,
        folderId,
        width: uploadDimensions.width,
        height: uploadDimensions.height,
      });
      URL.revokeObjectURL(uploadedImage.url);
      setUploadModalOpen(false);
      setUploadedImage(null);
      setUploadDimensions(null);
      setUploadFolderId(null);
      setUploadFolderTitle('');
      navigate(`/ai-avatar/my-avatars/folder/${folderId}`);
    } finally {
      setIsUploading(false);
    }
  }, [
    uploadedImage,
    uploadDimensions,
    uploadFolderId,
    uploadFolderTitle,
    createFolder,
    uploadAvatarMutation,
    navigate,
  ]);

  const handleUploadClose = useCallback(() => {
    if (uploadedImage) URL.revokeObjectURL(uploadedImage.url);
    setUploadModalOpen(false);
    setUploadedImage(null);
    setUploadDimensions(null);
    setUploadFolderId(null);
    setUploadFolderTitle('');
  }, [uploadedImage]);

  return {
    isFileUploading,
    uploadModalOpen,
    uploadedImage,
    isUploading,
    uploadFolderId,
    setUploadFolderId,
    uploadFolderTitle,
    setUploadFolderTitle,
    handleFileSelect,
    handleUploadSave,
    handleUploadClose,
  };
}
